import documentReady from '@document/ready';
import mitt from 'mitt';

export interface LaunchbaseConfig {
  integrationKey: string;
  theme?: {
    color?: string;
    textColor?: string;
  };
}

declare global {
  interface Window {
    launchbaseConfig?: LaunchbaseConfig;
    launchbase: typeof launchbaseInstance;
  }
}

let pageViewHost = 'https://launchbase-pageviews-api.varld.co';
let launchbaseEmbedHost = 'https://use.launchbase.work';
let css = `body.lb-open{overflow:hidden}.lb-wr{opacity:0;pointer-events:none;transition:opacity .3s}.lb-wr.lb-wr-open{opacity:1;pointer-events:all}.lb-bl{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.1);backdrop-filter:blur(10px);z-index:10000;cursor:default}.lb-fr-wr{position:fixed;top:0;bottom:0;left:0;right:0;z-index:10002;display:flex;flex-direction:column;justify-content:center;pointer-events:none}.lb-fr{border-radius:10px;transform:translateY(-20px);width:400px;height:300px;max-width:100vw;max-height:100vh;margin:0 auto;box-shadow:0 8px 30px rgba(0,0,0,.12);background:#fff;transition:transform .3s}.lb-wr.lb-wr-open .lb-fr{transform:translateY(0);pointer-events:all}.lb-wr.lb-qu .lb-fr{width:100vw;height:100vh;transition:all .3s}.lb-spin{border:4px solid #f3f3f3;border-top:4px solid #000;border-radius:50%;width:26px;height:26px;animation:lb-spin-anim 2s linear infinite;position:fixed;top:calc(50vh - 13px);left:calc(50vw - 13px);z-index:10001}@keyframes lb-spin-anim{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}@media screen and (max-width:500px){.lb-fr{border-radius:0;width:100vw;height:100vh}}`;

let setupStyles = () => {
  let styleEl = document.createElement('style');
  styleEl.type = 'text/css';

  // @ts-ignore
  if (styleEl.styleSheet) {
    // @ts-ignore
    styleEl.styleSheet.cssText = css;
  } else {
    styleEl.appendChild(document.createTextNode(css));
  }

  let head = document.head || document.getElementsByTagName('head')[0];
  head.appendChild(styleEl);
};

class Launchbase {
  private config: LaunchbaseConfig;
  public wrapperElement: HTMLDivElement;
  public iframeElement: HTMLIFrameElement;
  public visible: boolean;
  public emitter: ReturnType<typeof mitt>;
  private sendDataIv: number | NodeJS.Timeout;

  constructor(config: LaunchbaseConfig) {
    if (typeof window == 'undefined') return;

    if (!config || !config.integrationKey) {
      console.warn('[Launchbase]: Missing integration key or config');
      return;
    }

    this.config = config;
    this.visible = false;
    this.emitter = mitt();

    this.setupMessageListener();
    this.sendPageview();
    this.setupElements();
  }

  private sendPageview() {
    let request = new XMLHttpRequest();
    request.open(
      'get',
      `${pageViewHost}/count?integrationKey=${this.config.integrationKey}`,
      true
    );

    request.onload = () => {
      if (request.status == 404) {
        console.warn('[Launchbase]: Invalid integration token');
      }

      if (request.status == 200) {
        this.emitter.emit('pageview-sent');
      }
    };

    request.onerror = err => {
      this.emitter.emit('pageview-error', err);
      console.warn('[Launchbase]: Could not send pageview');
    };

    request.send();
  }

  private setupElements() {
    let wrapperEl = document.createElement('div');
    wrapperEl.style.display = 'none';
    wrapperEl.setAttribute('class', 'lb-wr');
    wrapperEl.setAttribute('data-lb-key', this.config.integrationKey);

    let blanketEl = document.createElement('div');
    blanketEl.setAttribute('class', 'lb-bl');
    blanketEl.addEventListener('click', () => close());

    let frameWrapperEl = document.createElement('div');
    frameWrapperEl.setAttribute('class', 'lb-fr-wr');

    let spinnerEl = document.createElement('div');
    spinnerEl.setAttribute('class', 'lb-spin');

    let frameEl = document.createElement('iframe');
    frameEl.setAttribute('class', 'lb-fr');
    frameEl.src = `${launchbaseEmbedHost}/${this.config.integrationKey}?client=lb:core`;
    frameEl.width = '100%';
    frameEl.height = '100%';
    frameEl.frameBorder = '0';
    frameEl.setAttribute('allowtransparency', '');
    frameEl.setAttribute(
      'sandbox',
      'allow-modals allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation'
    );
    frameEl.style.opacity = '0';
    frameEl.style.pointerEvents = 'none';
    frameEl.style.transform = 'translateY(-20px)';

    frameEl.addEventListener('load', () => {
      setTimeout(() => {
        frameEl.style.opacity = '';
        frameEl.style.pointerEvents = '';
        frameEl.style.transform = '';

        if (spinnerEl.parentElement) {
          spinnerEl.parentElement.removeChild(spinnerEl);
        }

        this.sendData();
      }, 100);
    });

    frameWrapperEl.appendChild(frameEl);
    wrapperEl.appendChild(frameWrapperEl);
    wrapperEl.appendChild(blanketEl);
    wrapperEl.appendChild(spinnerEl);

    let body = document.body || document.getElementsByTagName('body')[0];
    body.appendChild(wrapperEl);

    this.iframeElement = frameEl;
    this.wrapperElement = wrapperEl;
  }

  private sendData() {
    let frame = document.querySelector('.lb-fr') as HTMLIFrameElement;
    if (!frame || !this.config) return;

    let data = JSON.stringify({
      page: location.pathname,
      textColor: (this.config.theme || {}).textColor,
      color: (this.config.theme || {}).color
    });

    frame.contentWindow.postMessage(`lb:in;${data}`, '*');
  }

  private questionMode(enable: boolean) {
    this.emitter.emit('questionMode', enable);

    if (enable) {
      this.wrapperElement.classList.add('lb-qu');
    } else {
      this.wrapperElement.classList.remove('lb-qu');
    }
  }

  private setupMessageListener() {
    window.addEventListener('message', event => {
      if (event && typeof event.data == 'string') {
        if (event.data == 'lb:cl') {
          this.close();
        } else if (event.data == 'lb:qu') {
          this.questionMode(true);
        }
      }
    });
  }

  public open() {
    if (typeof window == 'undefined') return;
    this.sendData();

    this.wrapperElement.style.display = '';

    setTimeout(() => {
      this.wrapperElement.setAttribute('class', 'lb-wr lb-wr-open');
    }, 50);

    this.sendDataIv = setInterval(() => {
      this.sendData();
    }, 100);

    let body = document.body || document.getElementsByTagName('body')[0];
    body.classList.add('lb-open');

    this.visible = true;
    this.emitter.emit('open');
  }

  public close() {
    if (typeof window == 'undefined') return;
    this.wrapperElement.classList.remove('lb-wr-open');

    let body = document.body || document.getElementsByTagName('body')[0];
    body.classList.remove('lb-open');

    if (typeof this.sendDataIv == 'number') clearTimeout(this.sendDataIv);

    this.visible = false;
    this.emitter.emit('close');
  }

  public toggle() {
    this.emitter.emit('toggle');

    if (this.visible) {
      this.close();
    } else {
      this.open();
    }
  }
}

let instance: Launchbase;

let initHTML = () => {
  if (!instance) return;
  let elements = document.querySelectorAll('[data-launchbase]');

  for (let element of elements) {
    try {
      element.addEventListener('click', () => instance.toggle());
    } catch (err) {}
  }
};

/**
 * Set up the Launchbase instance
 * @param config Configuration data
 *
 * ```javascript
 * launchbase.setup({
 *   integrationKey: '{{integrationKey}}'
 * })
 * ```
 */
export let setup = (config: LaunchbaseConfig) => {
  if (typeof window == 'undefined') return;

  if (instance != undefined) {
    console.warn('[Launchbase]: Launchbase set up multiple times. Ignoring.');
    return instance;
  }

  instance = new Launchbase(config);

  return instance;
};

/**
 * Get the Launchbase instance
 */
export let getInstance = () => instance;

/**
 * Open the Launchbase widget
 *
 * ```javascript
 * launchbase.open();
 * // widget is now open
 * ```
 */
export let open = () => {
  if (!instance) {
    console.warn('[Launchbase]: Launchbase not set up. Call `launchbase.setup` first.');
    return;
  }

  instance.open();
};

/**
 * Close the Launchbase widget
 *
 * ```javascript
 * launchbase.close();
 * // widget is now closed
 * ```
 */
export let close = () => {
  if (!instance) {
    console.warn('[Launchbase]: Launchbase not set up. Call `launchbase.setup` first.');
    return;
  }

  instance.close();
};

/**
 * Open the widget if its closed or close it if its open.
 *
 * ```javascript
 * launchbase.toggle();
 * ```
 */
export let toggle = () => {
  if (!instance) {
    console.warn('[Launchbase]: Launchbase not set up. Call `launchbase.setup` first.');
    return;
  }

  instance.toggle();
};

let launchbaseInstance = {
  setup,
  open,
  close,
  toggle,
  getInstance
};

if (typeof window != 'undefined') {
  window.launchbase = launchbaseInstance;

  documentReady(() => {
    setupStyles();

    if (window.launchbaseConfig != undefined) {
      setup(window.launchbaseConfig);
      initHTML();
    }
  });
}

export default launchbaseInstance;
